type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export type DeliveryCostLocation = string | { deliveryLocationType: string } | null | undefined;

export type DeliveryCostValue = string | number | null | undefined;

interface DeliveryCostTerms {
  delivery_location?: string;
  delivery_cost?: DeliveryCostValue;
  // Set on the delivery round of the demand line whose payment covered the demand's cost.
  includes_delivery_cost?: boolean;
}

interface DeliveryCostOffer extends DeliveryCostTerms {
  demand?: number | null;
}

export type DeliveryCostSide = "AGGREGATOR" | "BUYER";

/** The demand-level delivery-cost summary every line of a demand carries (`demand_terms`). */
export interface DemandCostTerms {
  negotiable: boolean;
  delivery_cost: DeliveryCostValue;
  delivery_cost_locked: boolean;
  delivery_cost_paid: boolean;
  pending_delivery_cost_proposal: {
    side: DeliveryCostSide;
    delivery_cost: DeliveryCostValue;
  } | null;
}

const DELIVERY_COST_LOCATIONS = ["drop_at_trader", "custom_exchange_location"];

export const requiresDeliveryCost = (location: DeliveryCostLocation): boolean => {
  const type =
    location !== null && typeof location === "object" ? location.deliveryLocationType : location;
  return !!type && DELIVERY_COST_LOCATIONS.includes(type);
};

const isBlank = (value: DeliveryCostValue): boolean =>
  value === null || value === undefined || String(value).trim() === "";

export const deliveryCostIssue = (raw: DeliveryCostValue, t: TranslateFn): string | undefined => {
  if (isBlank(raw)) {
    return t("traderDetails.form.mandatoryFieldBlankError");
  }
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) {
    return t("marketOfferDetails.form.deliveryCostNegative");
  }
  if (!Number.isInteger(value)) {
    return t("marketOfferDetails.form.mustBeInteger");
  }
  return undefined;
};

export const toDeliveryCostPayload = (
  location: DeliveryCostLocation,
  raw: DeliveryCostValue
): number | null => (requiresDeliveryCost(location) && !isBlank(raw) ? Number(raw) : null);

export const deliveryCostAmount = (value: DeliveryCostValue): number => {
  const amount = Number(value);
  return isBlank(value) || !Number.isFinite(amount) ? 0 : amount;
};

export const hasDeliveryCost = (value: DeliveryCostValue): boolean => !isBlank(value);

/** A line of a multi-commodity demand: its delivery cost lives on the demand, not on the line. */
export const isDemandLine = (offer: { demand?: number | null }): boolean =>
  offer.demand !== null && offer.demand !== undefined;

export const effectiveDeliveryCost = (
  proposal: DeliveryCostTerms | undefined,
  offer: DeliveryCostOffer
): DeliveryCostValue => {
  const location = proposal ? proposal.delivery_location : offer.delivery_location;
  if (!requiresDeliveryCost(location)) {
    return null;
  }
  if (isDemandLine(offer)) {
    // The demand's single cost is negotiated on the demand; a line shows it only on the
    // delivery round of the line whose payment covered it.
    return proposal?.includes_delivery_cost ? (proposal.delivery_cost ?? null) : null;
  }
  return proposal?.delivery_cost ?? offer.delivery_cost ?? null;
};

/** The demand-level terms of a demand's lines: every line carries the same summary. */
export const demandTermsOf = <T extends DemandCostTerms>(
  offers: { demand_terms?: T | null }[]
): T | null => offers.find(offer => offer.demand_terms)?.demand_terms ?? null;

/**
 * The demand's delivery cost as it stands: the latest proposal still waiting for an answer (from
 * either side), else the agreed amount. Like a line's terms, which show its latest counter-offer.
 */
export const latestDemandDeliveryCost = (
  terms: DemandCostTerms | null | undefined
): DeliveryCostValue =>
  terms
    ? (terms.pending_delivery_cost_proposal?.delivery_cost ?? terms.delivery_cost ?? null)
    : null;

/** The demand's single delivery cost as it stands, as its lines carry it. */
export const demandDeliveryCost = (
  offers: { demand_terms?: DemandCostTerms | null }[]
): DeliveryCostValue => latestDemandDeliveryCost(demandTermsOf(offers));

/**
 * Where the demand's delivery cost stands, from the buyer's side:
 * - awaitingYou: the aggregator proposed an amount the buyer has to answer;
 * - awaitingAggregator: the buyer's own proposal is waiting for the aggregator;
 * - agreed: a negotiable cost is closed (demand accepted, or no line left to negotiate);
 * - locked: the cost was never negotiable;
 * - paid: a line's payment covered it.
 * Null while nothing is pending on an open cost, or when the demand carries no cost at all.
 */
export type DemandCostState = "awaitingYou" | "awaitingAggregator" | "agreed" | "locked" | "paid";

export const demandCostState = (
  terms: DemandCostTerms | null | undefined
): DemandCostState | null => {
  if (!terms) {
    return null;
  }
  if (terms.delivery_cost_paid) {
    return "paid";
  }
  if (terms.delivery_cost_locked) {
    if (!hasDeliveryCost(terms.delivery_cost)) {
      return null;
    }
    return terms.negotiable ? "agreed" : "locked";
  }
  const pending = terms.pending_delivery_cost_proposal;
  if (pending) {
    return pending.side === "AGGREGATOR" ? "awaitingYou" : "awaitingAggregator";
  }
  return null;
};
